import { z } from 'zod';
// Hugim percentages, with layered overrides resolved most-specific-first:
//   (coach, hug) > (coach, ANY) > (ANY, hug) > (ANY, ANY)
// where ANY is encoded as coach_arbox_users_boxes_id = 0 / hug_key = ''. The
// (0, '') row is the box's global default. See migration 012.
export const ANY_COACH = 0;
export const ANY_HUG = '';
/** Which seat on a course a percentage is for. Arbox's receipt report names a
 *  primary staff member and, optionally, a second one (`second_staff_member`);
 *  each is paid their own percentage of the same slice. Every level of the
 *  ladder exists once per role, and a role never borrows from the other. */
export const HugRoleSchema = z.enum(['primary', 'secondary']);
export const HugPercentSchema = z
    .number()
    .min(0)
    .max(100)
    .refine((n) => Number.isFinite(n), 'percent must be a number');
/** One configured percentage row. */
export const HugimPercentRowSchema = z.object({
    /** 0 = applies to any coach; else a specific arbox_users_boxes_id. */
    coach_arbox_users_boxes_id: z.number().int().min(0),
    /** '' = applies to any hug; else a stable hug key. */
    hug_key: z.string().max(200),
    percent: HugPercentSchema,
    /** Absent = 'primary' (every row written before the second staff member). */
    role: HugRoleSchema.optional(),
});
/** A row's role, with the pre-feature default. */
export function rowRole(r) {
    return r.role ?? 'primary';
}
const isDefaultRow = (r, role) => r.coach_arbox_users_boxes_id === ANY_COACH && r.hug_key === ANY_HUG && rowRole(r) === role;
const isAnyDefaultRow = (r) => r.coach_arbox_users_boxes_id === ANY_COACH && r.hug_key === ANY_HUG;
/** Normalize a course name into a stable hug key. Currently the identity Arbox
 *  gives us (no course id on the revenue report) — centralize here so a future
 *  id swap is one edit. */
export function hugKeyFromName(name) {
    return (name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}
/**
 * Resolve the percentage for a (coach, hug) pair against the configured rows.
 * Precedence: coach+hug > coach > hug > global(0,''), looked up among `role`'s
 * rows only. Returns `fallback` (0 by default) when nothing matches — so an
 * unconfigured box pays nothing until a percentage is set, and a secondary
 * percentage set nowhere is 0 rather than the primary's.
 */
export function resolveHugPercent(rows, coachId, hugKey, fallback = 0, role = 'primary') {
    const lookup = (c, h) => rows.find((r) => r.coach_arbox_users_boxes_id === c && r.hug_key === h && rowRole(r) === role)
        ?.percent;
    return (lookup(coachId, hugKey) ??
        lookup(coachId, ANY_HUG) ??
        lookup(ANY_COACH, hugKey) ??
        lookup(ANY_COACH, ANY_HUG) ??
        fallback);
}
/** Split a flat row list into the {default_percent, overrides} view. */
export function toHugimSettings(rows) {
    return {
        default_percent: rows.find((r) => isDefaultRow(r, 'primary'))?.percent ?? 0,
        secondary_default_percent: rows.find((r) => isDefaultRow(r, 'secondary'))?.percent ?? null,
        overrides: rows.filter((r) => !isAnyDefaultRow(r)),
    };
}
// ── מוצרים נלווים (add-ons) percentages ────────────────────────────────────
// A coach's cut of a receipt's add-ons, set apart from the course percentage
// but layered exactly like it: coach+hug > coach > hug > box default, with the
// same wildcards (coach 0 / hug ''). No row at any level → 0: nobody is paid on
// add-ons until someone sets a percentage. Table hugim_addon_percent_settings
// (migration 048).
export const HugimAddonPercentRowSchema = HugimPercentRowSchema;
/** Same precedence as the course percentage; nothing matches → `fallback` (0). */
export function resolveAddonPercent(rows, coachId, hugKey, fallback = 0, role = 'primary') {
    return resolveHugPercent(rows, coachId, hugKey, fallback, role);
}
export function toHugimAddonSettings(rows) {
    return {
        default_percent: rows.find((r) => isDefaultRow(r, 'primary'))?.percent ?? null,
        secondary_default_percent: rows.find((r) => isDefaultRow(r, 'secondary'))?.percent ?? null,
        overrides: rows.filter((r) => !isAnyDefaultRow(r)),
    };
}
//# sourceMappingURL=hugim-settings.js.map